/**
 * ! (☞ﾟヮﾟ)☞ Don't forget to update the readme when changing this file.
 */

// Feature flags that apply to all partners.
const staticFeatureFlags = {
  IS_VALIDATE_USERNAME_INPUT_CHARS_ENABLED: 'sc_249801',
  IS_PAY_PLAN_FILTERING_ENABLED: 't3_pay_plan_filtering',
  IS_FL_ENABLED: 'T3_FL',
  IS_KS_ENABLED: 'T3_KS',
  IS_UT_ENABLED: 'T3_UT',
  IS_T3_ENABLED: 'T3_AUTO',
  IS_PRIVACY_POLICY_V2_ENABLED: 'sc_271327',
  IS_CO_RTP_ENABLED: 'T1_CO_RTP',
  IS_T2_ENABLED: 'T2_AUTO',
  IS_PA_ENABLED: 'T2_PA',
  IS_PA_TAI_ENABLED: 'T2_PA_TAI',
  IS_VA_ENABLED: 'T2_VA',
  IS_VA_TAI_ENABLED: 'T2_VA_TAI',
  IS_WI_ENABLED: 'T2_WI',
  IS_WI_TAI_ENABLED: 'T2_WI_TAI',
  IS_IL_THEFT_FEE_CONFIG_ENABLED: 'toa_il_2023Q4',
  IS_SPEEDY_E2_ENABLED: 'sc_246751',
  IS_BRAZE_ENABLED: `is_braze_enabled`,
  IS_CREDIT_KARMA_OFFER_ENABLED: `m107416`,
  IS_EMAIL_MFA_ENABLED: 'is_email_mfa_enabled',
  IS_PARTNER_PHONE_NUMBER_ENABLED: `isTenantPhoneNumberEnabled`,
  IS_QUOTE_ASSIST_ENABLED: `isQuoteAssistEnabled`,
  IS_TOYOTA_RENTERS_ACCOUNT_CTA_HIDDEN: 'sc_209226',
  IS_TOYOTA_UPFRONT_PHONE_COLLECTION_ENABLED: 'sc_217215',
  IS_CO_EXPANSION_ENABLED: 'T1_CO',
  IS_CO_TAI_EXPANSION_ENABLED: 'T1_CO_TAI',
  IS_GA_TAI_EXPANSION_ENABLED: 'T1_GA_TAI',
  IS_GA_EXPANSION_ENABLED: 'T1_GA',
  IS_OR_EXPANSION_ENABLED: 'T1_OR',
  IS_OR_TAI_EXPANSION_ENABLED: 'T1_OR_TAI',
  IS_HOMEOWNERS_ENABLED: 'sc_237970',
  IS_LIGHTNING_LN_ADMIN_ENABLED: 'sc-241234',
  IS_AUTO_PROMOTION_ENABLED_FOR_HOMEOWNERS: 'sc_241821',
  IS_NEW_PAYMENT_SS_ENABLED: 'sc_266341',
  IS_PAYMENT_SERVICE_ENABLED: 'isTogglePaymentServiceEnabled',
  IS_CA_MIGRATION_AUTO_ENABLED: 'TOGGLE_AUTO_POLICY_MIGRATION',
  IS_MANUAL_ENROLLMENT_ENABLED: 'isManualEnrollmentEnabled',
  IS_DISABLE_EMAIL_FIELD_FOR_LINK_EXCHANGE: 'sc_289543',
  IS_RECAPTCHA_ENABLED: 'sc_294003',
  IS_AUTO_HCAPTCHA_ENABLED: 'sc_327992',
  IS_RENTERS_HCAPTCHA_ENABLED: 'renters_sc_327992',
  IS_RESTRICT_SAME_DAY_BINDING_ENABLED: 'sc_290368',
  SHOULD_USE_NEW_VGS_HOOKS: 'sc-295139',
  SHOULD_PERFORM_COVERAGE_CHECK_FOR_MIGRATED_POLICIES: 'AUT_MIG_COVERAGE_CHECK',
  SHOULD_DISABLE_AUTOPAY_TO_DB: 'sc_315323',
  IS_FARMERS_RENTERS_PAPER_PROGRAM_FEATURES_ENABLED: 'sc_329150',
  IS_AUTO_TX_PAPER_PROGRAM_EXPANSION_ENABLED: 'sc_325212',
  TOGGLE_RENTERS_TX_RATE_CHANGES: 'sc_323122', // TO-DO: remove this FF after TX Rate Change live sc-336090
} as const;

// Feature flags that need to be toggled indepdendently from partner to partner.
function generateFlagsForPartner(partner: 'CK' | 'Toggle' | 'Toyota') {
  const uppercased = partner.toUpperCase() as Uppercase<typeof partner>;

  // Key: the enum key we use within this repo.
  // Value: the value set in our feature flag API.
  const flags = {
    // Flags that turn individual features on or off.
    features: [
      {
        key: `IS_${uppercased}_ACCOUNT_MANAGEMENT_ENABLED`,
        value: `is${partner}AccountManagementEnabled`,
      },
      {
        key: `IS_${uppercased}_ACCOUNT_MANAGEMENT_ENABLED`,
        value: `is${partner}AccountManagementEnabled`,
      },
      {
        key: `IS_${uppercased}_ACCOUNT_MANAGEMENT_ENABLED`,
        value: `is${partner}AccountManagementEnabled`,
      },
      {
        key: `IS_${uppercased}_AUTO_PURCHASE_FLOW_ENABLED`,
        value: `is${partner}AutoPurchaseFlowEnabled`,
      },
      {
        key: `IS_${uppercased}_AUTO_QUOTE_LINK_EXCHANGE_ENABLED`,
        value: `is${partner}AutoQuoteLinkExchangeEnabled`,
      },
      {
        key: `IS_${uppercased}_AUTO_QUOTE_LINK_EXCHANGE_ENABLED`,
        value: `is${partner}AutoQuoteLinkExchangeEnabled`,
      },
      {
        key: `IS_${uppercased}_DIRECT_TO_AUTO_QUOTE_ENABLED`,
        value: `is${partner}DirectToAutoQuoteEnabled`,
      },
      {
        key: `IS_${uppercased}_LOGO_REDIRECT_IN_AUTO_PURCHASE_FLOW_ENABLED`,
        value: `is${partner}LogoRedirectInAutoPurchaseFlowEnabled`,
      },
      {
        key: `IS_${uppercased}_AUTO_PAPER_PROGRAM_ENABLED`,
        value: `is${partner}AutoPaperProgramEnabled`,
      },
      {
        key: `IS_${uppercased}_PRIVACY_AND_TERMS_ENABLED`,
        value: `is${partner}PrivacyAndTermsEnabled`,
      },
      {
        key: `IS_${uppercased}_BILLING_ENABLED`,
        value: `is${partner}BillingEnabled`,
      },
      {
        key: `IS_${uppercased}_SELF_SERVICE_ENABLED`,
        value: `is${partner}SelfServiceEnabled`,
      },
      {
        key: `IS_${uppercased}_RENTERS_QUOTE_CREATION_ENABLED`,
        value: `is${partner}RentersQuoteCreationEnabled`,
      },
      {
        key: `IS_${uppercased}_QUOTE_ASSIST_ENABLED`,
        value: `is${partner}QuoteAssistEnabled`,
      },
      {
        key: `IS_${uppercased}_THEME_DISABLED`,
        value: `is_${partner}_theme_disabled`,
      },
      {
        key: `IS_${uppercased}_QA_START_SCREEN_ENABLED`,
        value: `${partner}_qa_start_screen`,
      },
    ] as const,
    // Flags that enable or disable whole regions.
    stateExpansions: [
      {
        key: `IS_${uppercased}_TX_EXPANSION_ENABLED`,
        value: `is${partner}TXExpansionEnabled`,
      },
    ] as const,
  };

  type TKeys = typeof flags[keyof typeof flags][number]['key'];
  type TValues = typeof flags[keyof typeof flags][number]['value'];
  type TFlags = Record<TKeys, TValues>;

  return [...flags.features, ...flags.stateExpansions].reduce(
    (acc, curr) => ({ ...acc, [curr.key]: curr.value }),
    {} as TFlags
  );
}

export const featureFlag = {
  ...staticFeatureFlags,
  ...generateFlagsForPartner('CK'),
  ...generateFlagsForPartner('Toggle'),
  ...generateFlagsForPartner('Toyota'),
};

type EFeatureFlagKey = keyof typeof featureFlag;
export type EFeatureFlag = typeof featureFlag[EFeatureFlagKey];
